import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const SILK_IMG =
  "https://images.pexels.com/photos/7717491/pexels-photo-7717491.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Waitlist = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!valid) {
      toast.error("Please enter a valid email address.");
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.post(`${API}/waitlist`, {
        email: trimmed,
        source: "landing_page",
      });
      setSubmitted(true);
      if (data?.already_registered) {
        toast.success("You're already on the list.", {
          description: "We'll be in touch when the next capsule opens.",
        });
      } else {
        toast.success("You're on the list. Welcome to Kim Monroe.", {
          description: "Look out for an exclusive early-access invitation soon.",
        });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Invalid request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={SILK_IMG}
          alt="Silk texture"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 silk-overlay" />
        <div className="absolute inset-0 grain pointer-events-none" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-12 lg:px-16 py-28 sm:py-40 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
        >
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#E6CC98]" />
            <span className="text-xs uppercase tracking-[0.3em] gold-text font-body">
              Private Access
            </span>
            <span className="h-px w-10 bg-[#E6CC98]" />
          </div>

          <h2 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#FDFBF7] leading-[1.05] max-w-3xl mx-auto">
            The next collection drops
            <br />
            <span className="italic gold-gradient-text">by invitation only.</span>
          </h2>

          <p className="mt-8 text-base sm:text-lg text-[#A39791] font-body max-w-xl mx-auto leading-relaxed">
            Join the Kim Monroe waitlist for first access to limited capsule
            releases, atelier exclusives, and founders-circle pricing.
          </p>

          {!submitted ? (
            <form
              onSubmit={onSubmit}
              data-testid="waitlist-form"
              noValidate
              className="mt-12 max-w-xl mx-auto flex flex-col sm:flex-row items-stretch gap-3"
            >
              <input
                type="text"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="waitlist-email-input"
                className="glass-input flex-1 px-6 py-4 text-base font-body tracking-wide"
                aria-label="Email address"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="waitlist-submit-button"
                className="btn-gold px-8 py-4 text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Joining..." : "Request Access"}
                <ArrowRight size={16} weight="bold" />
              </button>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              data-testid="waitlist-success"
              className="mt-12 max-w-xl mx-auto border border-[#E6CC98]/30 bg-[#E6CC98]/5 backdrop-blur-md px-8 py-6 flex flex-col items-center gap-3"
            >
              <CheckCircle size={32} weight="thin" className="text-[#E6CC98]" />
              <p className="font-heading text-2xl text-[#FDFBF7]">
                Welcome to the atelier.
              </p>
              <p className="text-sm text-[#A39791] font-body">
                Your invitation will arrive at <span className="gold-text">{email}</span>.
              </p>
            </motion.div>
          )}

          <p className="mt-8 text-xs text-[#A39791]/70 font-body uppercase tracking-[0.25em]">
            No spam. Unsubscribe at any moment.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
